'use client';

import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { AddToCartVariantDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { LuPlus } from 'react-icons/lu';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
  customValuesError?: boolean;
  skipVariantDialog?: boolean;
  inCard?: boolean;
  fullSize?: boolean;
};

export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  quantity = 1,
  customValuesError = false,
  skipVariantDialog = false,
  inCard = false,
  fullSize = false,
  size,
  ...props
}: AddToCartButtonProps) {
  const isDesktop = useBreakpoint('md');
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);

  const addToCartMutation = useAddToCart();
  const [isAutoAdding, setIsAutoAdding] = useState(false);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const customValues = useVariantsStore(state => state.customValues);
  const selectedAccessories = useVariantsStore(state => state.selectedAccessories);
  const accessoriesQuantities = useVariantsStore(state => state.accessoriesQuantities);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'detail', locale, product.id, me?.customer.id ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES, locale],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies.data.find(currency => currency.symbol === currencySymbol)?.id ?? currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const firstSupplier = sortedSuppliers[0];
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (!isInitialized || priceOnRequest || (!sellable && quantity < 1)) return;

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    const desiredQuantity =
      product.min_quantity && product.min_quantity <= quantity
        ? quantity
        : (product.min_quantity ?? 1);

    if (fullProduct && (fullProduct.variants?.length || selectedAccessories?.length)) {
      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            state={{
              product: fullProduct,
              selectedPackages,
              selectedAccessories,
              accessoriesQuantities,
              selectedVariant,
              selectedQuantity,
              selectedServices,
              selectedValues,
              customValues,
              customValuesError,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={desiredQuantity}
              skipVariantDialog={skipVariantDialog}
              onAutoAddStateChange={setIsAutoAdding}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((s: SelectedServices) => ({ id: s.id }))
                : [],
              packages: selectedPackages
                ? selectedPackages.map(p => ({ id: p.id }) as CartItemPackages)
                : [],
            },
          },
          qty: desiredQuantity,
        },
      });
      const productPrice = priceDisplay.currentWithVat;
      if (productPrice != null) {
        trackEcommerce.addToCart({
          id: product.id.toString(),
          name: product.name,
          price: productPrice,
          currency: currencyISO,
        });
      }
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  const buttonSize = fullSize || (inCard && isDesktop) ? 'default' : 'icon-scaling';
  const effectiveSize = size ?? buttonSize;
  const hideText =
    (typeof effectiveSize === 'string' && effectiveSize.includes('icon')) ||
    (!fullSize && inCard && !isDesktop);

  const buttonContent = (
    <>
      {(showIcon || hideText) && <LuPlus size={20} className="text-white" />}
      <span className={cn('line-clamp-1 text-ellipsis text-sm font-medium', hideText && 'hidden')}>
        {customText ? t(customText) : t('text1115')}
      </span>
    </>
  );

  if (!firstSupplier?.is_affiliate && priceOnRequest) return null;

  if (firstSupplier?.is_affiliate) {
    return (
      <Button
        className={cn(className)}
        asChild
        variant="default"
        size={effectiveSize}
        isLoading={addToCartMutation.isPending || productQuery.isFetching}
        {...props}
      >
        <a href={firstSupplier.url ?? '#'}>
          {showIcon && <LuPlus size={16} className="text-white" />}
          <span className="line-clamp-1 text-ellipsis text-center">
            {customText ? t(customText) : t('text1226')}
          </span>
        </a>
      </Button>
    );
  }

  return (
    <Button
      className={cn(className)}
      aria-label={customText ? t(customText) : t('text1115')}
      onClick={e => {
        e.stopPropagation();
        e.nativeEvent.preventDefault();
        handleAddToCart();
      }}
      disabled={!isInitialized || !sellable || customValuesError}
      variant="default"
      size={effectiveSize}
      isLoading={addToCartMutation.isPending || productQuery.isFetching || isAutoAdding}
      {...props}
    >
      {buttonContent}
    </Button>
  );
}
